import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { parseReplay, ParsedReplay, getDurationSeconds, frameIndexAtTick, KillEvent, PlayerHurtEvent, BombPlantEvent, BombDefuseStartEvent, SmokeThrowEvent, EntityStartEvent, SmokeBreak } from './replayParser';
import { fetchReplayRound, checkReplayAvailability } from '../../../services/ReplayService';
import { getClipRequestsForRound, SmokeClipRequestDTO } from '../../../services/SmokeReplayService';
import { AuthContext } from '../../../context/AuthContext';
import { admins, MAP_CONTROL_PUBLIC } from '../../../Constants';
import ReplayCanvas from './ReplayCanvas';
import { useMapControl } from './mapControl/useMapControl';
import { multiLevelMaps } from '../../../content/maps/mapData';
import ReplayControls from './ReplayControls';
import ReplayScoreboard from './ReplayScoreboard';
import ReplayKillFeed from './ReplayKillFeed';
import ReplayScoreHeader from './ReplayScoreHeader';
import ReplaySwingBar from './ReplaySwingBar';
import { SwingBreakdown } from '../../../models/SwingBreakdown';
import SmokeDetailPanel from './SmokeDetailPanel';
import ReplayShortcutsHelp from './ReplayShortcutsHelp';
import { useReplayShortcuts, ReplayHold, REPLAY_SPEEDS } from './useReplayShortcuts';
import { team1IsCtInRound, teamWonRound } from '../../MatchPage2/matchStats';
import { track } from '../../../utils/track';

interface ReplayViewerProps {
    season: number;
    matchId: string;
    gameId: string;
    mapName: string;
    playerNames?: Record<string, string>;
    team1Name?: string;
    team2Name?: string;
    roundEnds?: { [round: number]: {
        reason: string; teamWonID: string | null;
        tBuyType?: string | null; ctBuyType?: string | null;
        tEquipValue?: number | null; ctEquipValue?: number | null;
    } };
    team1Id?: string;
    // Lets a stale round-end winner id be spotted (see teamWonRound).
    team2Id?: string;
    team1SteamIds?: string[];
    team1Logo?: string;
    team2Logo?: string;
    // Optional overrides so private (donator upload) viewers can route replay fetches through
    // an access-controlled endpoint instead of the public ReplayService.
    fetchRoundOverride?: (season: number, matchId: string, gameId: string, roundNumber: number) => Promise<ArrayBuffer>;
    checkAvailabilityOverride?: (gameId: string) => Promise<{ available: boolean; roundCount: number }>;
    // Where to open, read once on mount (deep links, see replayLink.ts). `initialTime` is seconds
    // since the round went live. `initialFocusSteamId` opens 3 s before that player's first kill.
    initialRound?: number;
    initialTime?: number | null;
    initialFocusSteamId?: string | null;
    onRoundChange?: (round: number) => void;
    // Off on the owner-only upload page: nobody else can open that URL.
    canCopyLink?: boolean;
    // The map's per-event swing (match_swing_events). Turns on the Swing toggle (live odds + kill swing).
    swing?: SwingBreakdown | null;
}

export type ReplayLayout = 'split' | 'stacked' | 'mobile';

const SWING_KEY = 'replaySwing'; // the Swing toggle, remembered per browser
const POST_ROUND_TICKS = 96; // ReplayDataCollector.POST_ROUND_EXTRA_TICKS: frames kept after the round ends

async function decompressGzip(data: ArrayBuffer): Promise<ArrayBuffer> {
    const ds = new DecompressionStream('gzip');
    const writer = ds.writable.getWriter();
    writer.write(new Uint8Array(data));
    writer.close();

    const reader = ds.readable.getReader();
    const chunks: Uint8Array[] = [];
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
    }

    const totalLength = chunks.reduce((acc, c) => acc + c.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
    }
    return result.buffer;
}

const ReplayViewer: React.FC<ReplayViewerProps> = ({
    season, matchId, gameId, mapName, playerNames = {},
    team1Name = 'Team 1', team2Name = 'Team 2',
    roundEnds, team1Id, team2Id, team1SteamIds = [],
    team1Logo, team2Logo,
    fetchRoundOverride, checkAvailabilityOverride,
    initialRound = 1, initialTime = null, initialFocusSteamId = null, onRoundChange, canCopyLink = true,
    swing = null,
}) => {
    const fetchRound = fetchRoundOverride ?? fetchReplayRound;
    const checkAvailability = checkAvailabilityOverride ?? checkReplayAvailability;
    const [replay, setReplay] = useState<ParsedReplay | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentFrame, setCurrentFrame] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [selectedRound, setSelectedRound] = useState(initialRound);
    // The linked moment, applied once its round has loaded. Opens paused, like the sender saw it.
    const pendingJumpRef = useRef<{ round: number; time: number | null; steamId: string | null } | null>(
        initialTime !== null || initialFocusSteamId ? { round: initialRound, time: initialTime, steamId: initialFocusSteamId } : null);
    const [roundCount, setRoundCount] = useState(0);
    // "gameId:round" that `replay` holds. Differs from the selection while a round is downloading.
    const [loadedKey, setLoadedKey] = useState('');
    const [zoom, setZoom] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanMode, setIsPanMode] = useState(true);
    const [layout, setLayout] = useState<ReplayLayout>('split');
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 991);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showMapControl, setShowMapControl] = useState(false);
    const [showSwing, setShowSwing] = useState<boolean>(() => {
        try { return localStorage.getItem(SWING_KEY) === '1'; } catch { return false; }
    });
    const toggleSwing = useCallback(() => {
        setShowSwing(on => {
            try { localStorage.setItem(SWING_KEY, on ? '0' : '1'); } catch { /* just won't persist */ }
            return !on;
        });
    }, []);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 991px)');
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
        const handler = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    // Set by onMouseEnter/Leave on the container. Keyboard shortcuts only fire while this is true
    // (or in fullscreen), so the arrow keys still scroll the page when the mouse is elsewhere.
    const [isHovered, setIsHovered] = useState(false);
    const [hold, setHold] = useState<ReplayHold | null>(null);
    const holdWasPlayingRef = useRef(false);
    const [showShortcuts, setShowShortcuts] = useState(false);

    const handleFullscreenToggle = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        if (document.fullscreenElement === el) {
            document.exitFullscreen().catch(() => {});
        } else {
            el.requestFullscreen().catch(() => {});
        }
    }, []);

    const effectiveLayout: ReplayLayout = isMobile ? 'mobile' : layout;

    // Smoke detail panel state
    // NOTE: If clip requests are opened to all users in the future, smoke click/detail
    // panel access should stay restricted to admins and beta users only.
    const { user, token, isBeta } = useContext(AuthContext);
    const isAdmin = !!(user && admins.includes(user.steamID));
    // Map control is dark-launched: beta testers and admins only until MAP_CONTROL_PUBLIC flips.
    const canSeeMapControl = MAP_CONTROL_PUBLIC || isBeta || isAdmin;
    const canUseSmokes = isAdmin || isBeta;
    const [selectedSmokeEntityId, setSelectedSmokeEntityId] = useState<number | null>(null);
    const [clipRequests, setClipRequests] = useState<SmokeClipRequestDTO[]>([]);

    const animFrameRef = useRef<number>(0);
    const lastTimeRef = useRef<number>(0);
    const accumRef = useRef<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const playTrackedRef = useRef<string>('');
    // Tracks currentFrame synchronously inside the playback tick — state updates
    // get batched, so a ref is the only way to know the latest frame mid-loop.
    const currentFrameRef = useRef<number>(0);
    // "gameId:round" most recently asked for, so a slow download cannot replace a newer pick. The
    // game is part of the key because the viewer is reused when a Bo3 switches map.
    const requestedKeyRef = useRef<string>('');

    useEffect(() => { currentFrameRef.current = currentFrame; }, [currentFrame]);

    // Round cache — preload all rounds, clear when gameId changes
    const roundCacheRef = useRef<Map<number, ParsedReplay>>(new Map());
    const cacheGameIdRef = useRef<string>('');

    useEffect(() => {
        checkAvailability(gameId).then(result => {
            setRoundCount(result.roundCount);
            // A linked round past the last one falls back to round 1 instead of a load error.
            setSelectedRound(r => (r <= result.roundCount ? r : 1));
            if (result.roundCount > 0) {
                track('replay-open', { matchId, gameId, map: mapName });
            }
        });
    }, [gameId, matchId, mapName]);

    // Clear cache and reset round when game changes. The first run is the mount, which keeps
    // initialRound.
    useEffect(() => {
        if (cacheGameIdRef.current !== gameId) {
            const isSwitch = cacheGameIdRef.current !== '';
            roundCacheRef.current.clear();
            cacheGameIdRef.current = gameId;
            if (isSwitch) {
                setSelectedRound(1);
                setIsPlaying(false);
                pendingJumpRef.current = null;
            }
        }
    }, [gameId]);

    // Mirrors the round up so the page can keep ?round= in the URL. One-way: the page never
    // pushes it back down, it only hands initialRound to a fresh mount.
    useEffect(() => { onRoundChange?.(selectedRound); }, [selectedRound, onRoundChange]);

    // Preload all rounds in background
    useEffect(() => {
        if (roundCount <= 0) return;
        let cancelled = false;

        const preloadAll = async () => {
            for (let r = 1; r <= roundCount; r++) {
                if (cancelled) break;
                if (roundCacheRef.current.has(r)) continue;
                try {
                    const compressed = await fetchRound(season, matchId, gameId, r);
                    if (cancelled) break;
                    const decompressed = await decompressGzip(compressed);
                    const parsed = parseReplay(decompressed);
                    roundCacheRef.current.set(r, parsed);
                } catch { /* skip failed rounds */ }
            }
        };

        preloadAll();
        return () => { cancelled = true; };
    }, [matchId, gameId, roundCount]);

    // Changing round never changes play/pause: a playing replay carries on into the new round,
    // whether the change came from the round end, a round button or the keyboard.
    const loadRound = useCallback(async (round: number) => {
        const requestKey = `${gameId}:${round}`;
        requestedKeyRef.current = requestKey;

        // Use cache if available — instant switch
        const cached = roundCacheRef.current.get(round);
        if (cached) {
            setReplay(cached);
            setLoadedKey(requestKey);
            setCurrentFrame(0);
            currentFrameRef.current = 0;
            setZoom(1);
            setPanOffset({ x: 0, y: 0 });
            setError(null);
            setLoading(false);
            return;
        }

        // Fallback: fetch on demand
        setLoading(true);
        setError(null);

        try {
            const compressed = await fetchRound(season, matchId, gameId, round);
            const decompressed = await decompressGzip(compressed);
            const parsed = parseReplay(decompressed);
            if (cacheGameIdRef.current === gameId) roundCacheRef.current.set(round, parsed);
            // A newer round was picked while this one downloaded; that one owns the view.
            if (requestedKeyRef.current !== requestKey) return;
            setReplay(parsed);
            setLoadedKey(requestKey);
            setCurrentFrame(0);
            currentFrameRef.current = 0;
            setZoom(1);
            setPanOffset({ x: 0, y: 0 });
        } catch (err: any) {
            if (requestedKeyRef.current !== requestKey) return;
            setError(err.message || 'Failed to load replay data');
            setReplay(null);
            setIsPlaying(false);
        } finally {
            if (requestedKeyRef.current === requestKey) setLoading(false);
        }
    }, [matchId, gameId]);

    useEffect(() => {
        if (roundCount > 0) {
            loadRound(selectedRound);
        }
    }, [selectedRound, roundCount, loadRound]);

    // Playback loop. It only runs once `replay` holds the selected round: while the next round is
    // still downloading, the old one would otherwise keep playing, hit its end again and skip a round.
    useEffect(() => {
        if (!isPlaying || !replay || loadedKey !== `${gameId}:${selectedRound}`) return;

        const tickRate = Math.max(replay.header.tickRate, 1);
        const direction = hold === 'rewind' ? -1 : 1;
        const effectiveSpeed = hold === 'fastForward' ? speed * 2 : speed;

        const tick = (timestamp: number) => {
            if (lastTimeRef.current === 0) {
                lastTimeRef.current = timestamp;
            }

            const delta = timestamp - lastTimeRef.current;
            lastTimeRef.current = timestamp;
            accumRef.current += delta * effectiveSpeed;

            if (replay.frames.length > 1) {
                const avgTicksPerFrame = replay.frames[replay.frames.length - 1].tick / (replay.frames.length - 1);
                const msPerFrame = (avgTicksPerFrame / tickRate) * 1000;

                let endHit = false;
                while (accumRef.current >= msPerFrame && !endHit) {
                    accumRef.current -= msPerFrame;
                    const nextFrame = currentFrameRef.current + direction;
                    if (nextFrame < 0) {
                        // Rewind stops at the round start; it never steps back a round.
                        accumRef.current = 0;
                        break;
                    }
                    if (nextFrame >= replay.frames.length) {
                        endHit = true;
                    } else {
                        currentFrameRef.current = nextFrame;
                        setCurrentFrame(nextFrame);
                    }
                }

                if (endHit) {
                    accumRef.current = 0;
                    if (selectedRound < roundCount) {
                        setSelectedRound(r => r + 1);
                    } else {
                        setIsPlaying(false);
                    }
                    return;
                }
            }

            animFrameRef.current = requestAnimationFrame(tick);
        };

        lastTimeRef.current = 0;
        accumRef.current = 0;
        animFrameRef.current = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(animFrameRef.current);
    }, [isPlaying, replay, loadedKey, gameId, speed, hold, selectedRound, roundCount]);

    const handlePlayPause = () => {
        if (!isPlaying && playTrackedRef.current !== gameId) {
            playTrackedRef.current = gameId;
            track('replay-play', { matchId, gameId, map: mapName });
        }
        setIsPlaying(!isPlaying);
    };
    const handleSeek = useCallback((frame: number) => {
        // Set the ref too, so a seek during playback is not overwritten by the next tick.
        currentFrameRef.current = frame;
        setCurrentFrame(frame);
        accumRef.current = 0;
    }, []);
    const handleResetView = useCallback(() => {
        setZoom(1);
        setPanOffset({ x: 0, y: 0 });
    }, []);

    // Apply the linked moment once its round is the one on screen. Picking another round first
    // cancels it, so it cannot fire later when that round is picked again.
    useEffect(() => {
        const jump = pendingJumpRef.current;
        if (!jump) return;
        if (selectedRound !== jump.round) { pendingJumpRef.current = null; return; }
        if (!replay || replay.frames.length === 0 || loadedKey !== `${gameId}:${jump.round}`) return;
        pendingJumpRef.current = null;
        const tickRate = Math.max(replay.header.tickRate, 1);
        const startTick = replay.frames[0].tick;
        let target = startTick + (jump.time ?? 0) * tickRate;
        if (jump.steamId) {
            const slot = replay.roster.findIndex(r => r.steamId === jump.steamId);
            const kill = replay.events.find((e): e is KillEvent => e.type === 'kill' && e.killerSlot === slot);
            if (slot >= 0 && kill) target = (replay.frames[kill.frameIdx]?.tick ?? startTick) - 3 * tickRate;
        }
        handleSeek(frameIndexAtTick(replay.frames, target));
    }, [replay, loadedKey, gameId, selectedRound, handleSeek]);

    const [linkToast, setLinkToast] = useState<string | null>(null);
    const linkToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (linkToastTimer.current) clearTimeout(linkToastTimer.current); }, []);

    // The current page URL with this round and moment. Whole seconds, rounded down, so the link
    // lands just before what the sender was looking at.
    const handleCopyLink = useCallback(() => {
        if (!replay || replay.frames.length === 0) return;
        const frame = replay.frames[currentFrameRef.current] ?? replay.frames[0];
        const seconds = Math.floor((frame.tick - replay.frames[0].tick) / Math.max(replay.header.tickRate, 1));
        const url = new URL(window.location.href);
        url.searchParams.set('tab', 'replay');
        url.searchParams.set('round', String(selectedRound));
        url.searchParams.set('t', String(seconds));
        const show = (msg: string) => {
            if (linkToastTimer.current) clearTimeout(linkToastTimer.current);
            setLinkToast(msg);
            linkToastTimer.current = setTimeout(() => setLinkToast(null), 2500);
        };
        navigator.clipboard.writeText(url.toString())
            .then(() => show(`Link copied: round ${selectedRound}, ${seconds}s in`))
            .catch(() => show('Could not copy the link'));
        track('replay-copy-link', { matchId, gameId });
    }, [replay, selectedRound, matchId, gameId]);

    useReplayShortcuts(!!replay && (isHovered || isFullscreen), {
        togglePlay: handlePlayPause,
        seekSeconds: (seconds) => {
            if (!replay || replay.frames.length === 0) return;
            const frame = replay.frames[currentFrameRef.current] ?? replay.frames[0];
            handleSeek(frameIndexAtTick(replay.frames, frame.tick + seconds * Math.max(replay.header.tickRate, 1)));
        },
        stepFrame: (delta) => {
            if (!replay || replay.frames.length === 0) return;
            setIsPlaying(false);
            handleSeek(Math.min(replay.frames.length - 1, Math.max(0, currentFrameRef.current + delta)));
        },
        changeRound: (delta) => {
            const next = selectedRound + delta;
            if (next >= 1 && next <= roundCount) setSelectedRound(next);
        },
        changeSpeed: (delta) => {
            const i = REPLAY_SPEEDS.indexOf(speed);
            const next = (i < 0 ? REPLAY_SPEEDS.indexOf(1) : i) + delta;
            setSpeed(REPLAY_SPEEDS[Math.min(REPLAY_SPEEDS.length - 1, Math.max(0, next))]);
        },
        jumpToRoundStart: () => handleSeek(0),
        copyLink: () => { if (canCopyLink) handleCopyLink(); },
        toggleFullscreen: handleFullscreenToggle,
        resetView: handleResetView,
        toggleHelp: () => setShowShortcuts(v => !v),
        closeHelp: () => {
            if (!showShortcuts) return false;
            setShowShortcuts(false);
            return true;
        },
        // A hold plays even from pause, and puts play/pause back the way it was on release.
        startHold: (h) => {
            holdWasPlayingRef.current = isPlaying;
            accumRef.current = 0;
            setHold(h);
            setIsPlaying(true);
        },
        endHold: () => {
            setHold(null);
            setIsPlaying(holdWasPlayingRef.current);
        },
    });

    // Fetch existing clip requests when round changes (all users — for clip indicator icons)
    useEffect(() => {
        setSelectedSmokeEntityId(null);
        getClipRequestsForRound(gameId, selectedRound).then(setClipRequests);
    }, [gameId, selectedRound]);

    // Match a smoke entity ID to its SmokeThrowEvent from the replay binary
    const findSmokeThrowForEntity = useCallback((entityId: number): SmokeThrowEvent | null => {
        if (!replay || !replay.smokeThrows || replay.smokeThrows.length === 0) return null;

        // Find the smoke_start event for this entity to get thrower slot and frame
        const smokeStart = replay.events.find(
            (e): e is EntityStartEvent => e.type === 'smoke_start' && e.entityId === entityId
        );
        if (!smokeStart) return null;

        // Find the most recent SmokeThrow from the same thrower that happened before the detonation
        const candidates = replay.smokeThrows
            .filter(t => t.throwerSlot === smokeStart.throwerSlot && t.frameIdx <= smokeStart.frameIdx)
            .sort((a, b) => b.frameIdx - a.frameIdx);

        return candidates[0] ?? null;
    }, [replay]);

    const handleSmokeClick = useCallback((entityId: number) => {
        setSelectedSmokeEntityId(prev => prev === entityId ? null : entityId);
    }, []);

    // Current selected smoke data
    const selectedSmokeThrow = useMemo(
        () => selectedSmokeEntityId !== null ? findSmokeThrowForEntity(selectedSmokeEntityId) : null,
        [selectedSmokeEntityId, findSmokeThrowForEntity]
    );

    const selectedSmokeStart = useMemo(() => {
        if (!replay || selectedSmokeEntityId === null) return null;
        return replay.events.find(
            (e): e is EntityStartEvent => e.type === 'smoke_start' && e.entityId === selectedSmokeEntityId
        ) ?? null;
    }, [replay, selectedSmokeEntityId]);

    const selectedSmokeClipRequest = useMemo(() => {
        if (!selectedSmokeThrow) return null;
        return clipRequests.find(
            r => r.throwerSlot === selectedSmokeThrow.throwerSlot && r.throwTick === selectedSmokeThrow.absoluteTick
        ) ?? null;
    }, [clipRequests, selectedSmokeThrow]);

    // Build set of smoke entity IDs that have completed clips (for play-button badge)
    const smokeClipEntityIds = useMemo<Set<number>>(() => {
        const ids = new Set<number>();
        if (!replay || clipRequests.length === 0) return ids;

        for (const req of clipRequests) {
            if (req.clipStatus !== 'completed') continue;
            // Find the matching smoke throw by thrower slot + absolute tick
            const throwEvt = replay.smokeThrows?.find(
                t => t.throwerSlot === req.throwerSlot && t.absoluteTick === req.throwTick
            );
            if (!throwEvt) continue;
            // Find the smoke_start event that this throw produced
            const smokeStart = replay.events.find(
                (e): e is EntityStartEvent =>
                    e.type === 'smoke_start' &&
                    e.throwerSlot === throwEvt.throwerSlot &&
                    e.frameIdx >= throwEvt.frameIdx
            );
            if (smokeStart) ids.add(smokeStart.entityId);
        }
        return ids;
    }, [replay, clipRequests]);

    const cleanMapName = mapName.replace('*', '');

    // Map-control field for the loaded round. Computed in a worker the first time the overlay is
    // enabled, then cached until the round changes.
    //
    // Split-level maps get one field per floor: a single 2D grid collapses de_nuke's upper and lower
    // levels onto the same XY. The height bands overlap through the ramp so each floor's graph stays
    // connected, and players on the other floor are excluded so they cannot claim ground below them.
    // Geometry planes overlap through the ramp so each floor's graph stays connected, but PLAYERS
    // must belong to exactly one floor. Sharing the overlapping band let a T standing in lower B
    // (z ~ -600, still above zLower) also count on the upper plane and project control there.
    const ml = multiLevelMaps[cleanMapName];
    const zSplit = ml ? (ml.zUpper + ml.zLower) / 2 : 0;
    const upperBand = useMemo(() => (ml ? { zMin: zSplit } : undefined), [ml, zSplit]);
    const lowerBand = useMemo(() => (ml ? { zMax: zSplit } : undefined), [ml, zSplit]);
    const mapControl = useMapControl(
        replay, ml ? `${cleanMapName}_upper` : cleanMapName, showMapControl, upperBand);
    const mapControlLower = useMapControl(
        replay, ml ? `${cleanMapName}_lower` : '', showMapControl && !!ml, lowerBand);

    // Beta swing overlay: the loaded round's v3 swing events placed on the replay. Their time is seconds since the
    // freeze end, which is the replay's tick 0 too (checked against every kill of a map: within 0.05 s), and their
    // odds are the T side's, the side the replay draws by, so no team mapping is needed. Keyed on the replay's own
    // round number: while the next round downloads, `replay` still holds the previous one.
    const swingTrack = useMemo(() => {
        const round = replay && swing?.rounds.find(r => r.n === replay.header.roundNumber);
        if (!swing || !replay || replay.frames.length === 0 || !round || round.events.length === 0) return null;
        const rate = Math.max(replay.header.tickRate, 1);
        const start = replay.frames[0].tick;
        // the round end has no time of its own: it lands where the post-round buffer starts
        const endFrame = Math.max(0, replay.frames.length - 1 - POST_ROUND_TICKS);
        const steps: { frame: number; t: number; decided: boolean }[] = [{ frame: 0, t: round.events[0].wtb, decided: false }];
        const byVictim = new Map<string, { dT: number; frozen: boolean }>(); // victim steam id -> T odds change
        let decided = false;
        for (const e of round.events) {
            const prev = steps[steps.length - 1].frame;
            const at = e.s != null ? frameIndexAtTick(replay.frames, start + Math.round(e.s * rate)) : Math.max(endFrame, prev);
            decided = decided || !!e.frozen;
            steps.push({ frame: Math.max(at, prev), t: e.wta, decided });
            const victim = e.vi != null ? swing.players[e.vi]?.id : undefined;
            if (victim && (e.type === 'K' || e.type === 'TK' || e.type === 'SU')) {
                byVictim.set(victim, { dT: e.wta - e.wtb, frozen: !!e.frozen });
            }
        }
        return { steps, byVictim };
    }, [swing, replay]);
    const swingOn = showSwing && swingTrack !== null;

    // Build kill feed data
    const killFeedData = useMemo(() => {
        if (!replay) return [];
        return replay.events
            .filter((e): e is KillEvent => e.type === 'kill')
            .map(k => ({
                killerSlot: k.killerSlot,
                victimSlot: k.victimSlot,
                weaponId: k.weaponId,
                headshot: k.headshot,
                wallbang: k.wallbang,
                noscope: k.noscope,
                thrusmoke: k.thrusmoke,
                frameIdx: k.frameIdx,
                killerName: (() => {
                    const entry = replay.roster[k.killerSlot];
                    return entry ? playerNames[entry.steamId] || `P${k.killerSlot + 1}` : `P${k.killerSlot + 1}`;
                })(),
                victimName: (() => {
                    const entry = replay.roster[k.victimSlot];
                    return entry ? playerNames[entry.steamId] || `P${k.victimSlot + 1}` : `P${k.victimSlot + 1}`;
                })(),
                killerTeam: replay.roster[k.killerSlot]?.team || 'T',
                victimTeam: replay.roster[k.victimSlot]?.team || 'CT',
                swing: (() => {
                    const sw = swingOn ? swingTrack.byVictim.get(replay.roster[k.victimSlot]?.steamId ?? '') : undefined;
                    if (!sw) return undefined;
                    const killerIsT = (replay.roster[k.killerSlot]?.team || 'T') === 'T';
                    return { delta: killerIsT ? sw.dT : -sw.dT, frozen: sw.frozen };
                })(),
            }));
    }, [replay, playerNames, swingOn, swingTrack]);

    // Bomb plant frame
    const bombPlantFrame = useMemo(() => {
        if (!replay) return null;
        const plantEvent = replay.events.find((e): e is BombPlantEvent => e.type === 'bomb_plant');
        return plantEvent ? plantEvent.frameIdx : null;
    }, [replay]);

    // Bomb explosion: position comes from the plant event (planted C4 doesn't move).
    // World-state bombX/Y becomes 0 once CPlantedC4 is destroyed, so we can't rely on it here.
    const bombExploded = useMemo<{ frameIdx: number; x: number; y: number } | null>(() => {
        if (!replay) return null;
        const plant = replay.events.find((e): e is BombPlantEvent => e.type === 'bomb_plant');
        const explode = replay.events.find(e => e.type === 'bomb_exploded');
        if (!plant || !explode) return null;
        return { frameIdx: explode.frameIdx, x: plant.x, y: plant.y };
    }, [replay]);

    // Active defuse: find the most recent bomb_begin_defuse that's still active
    const activeDefuse = useMemo<{ startFrame: number; hasKit: boolean } | null>(() => {
        if (!replay || bombPlantFrame === null || currentFrame < bombPlantFrame) return null;
        const defuseStarts = replay.events
            .filter((e): e is BombDefuseStartEvent => e.type === 'bomb_begin_defuse')
            .filter(e => e.frameIdx <= currentFrame);
        if (defuseStarts.length === 0) return null;
        const lastDefuse = defuseStarts[defuseStarts.length - 1];
        const frame = replay.frames[currentFrame];
        if (!frame || lastDefuse.defuserSlot >= frame.players.length) return null;
        const defuser = frame.players[lastDefuse.defuserSlot];
        if (!defuser || !defuser.isAlive || !(defuser.flags & (1 << 2))) return null; // bit 2 = isDefusing
        return { startFrame: lastDefuse.frameIdx, hasKit: lastDefuse.hasKit };
    }, [replay, currentFrame, bombPlantFrame]);

    // Determine which team is T/CT this round
    const team1IsT = useMemo(() => {
        if (!replay || team1SteamIds.length === 0) return true;
        const tRosterIds = replay.roster.filter(r => r.team === 'T').map(r => r.steamId);
        return tRosterIds.some(id => team1SteamIds.includes(id));
    }, [replay, team1SteamIds]);

    // Compute running T/CT scores entering the selected round
    const { tScore, ctScore } = useMemo(() => {
        if (!roundEnds || !team1Id) return { tScore: 0, ctScore: 0 };
        let t1Wins = 0, t2Wins = 0;
        for (let r = 1; r < selectedRound; r++) {
            const end = roundEnds[r];
            // teamWonID is empty on uploaded demos, so teamWonRound falls back to the
            // round-end reason (team1 starts CT).
            if (!end) continue;
            if (teamWonRound(end.reason, end.teamWonID, team1Id, team2Id, team1IsCtInRound(r))) t1Wins++;
            else t2Wins++;
        }
        return {
            tScore: team1IsT ? t1Wins : t2Wins,
            ctScore: team1IsT ? t2Wins : t1Wins,
        };
    }, [roundEnds, team1Id, team2Id, selectedRound, team1IsT]);

    // Smoke breaks — derived client-side: HE detonations within 200 world units
    // of an active smoke at the same frame partially clear it for ~1.5s.
    // De-duplicated per (frameIdx, smokeEntityId) — one HE that hits two overlapping
    // smokes still produces one timeline marker per smoke since they're separate events.
    const smokeBreaks = useMemo<SmokeBreak[]>(() => {
        if (!replay) return [];
        const HE_TO_SMOKE_RADIUS = 200;
        const radiusSq = HE_TO_SMOKE_RADIUS * HE_TO_SMOKE_RADIUS;
        const breaks: SmokeBreak[] = [];
        for (const ev of replay.events) {
            if (ev.type !== 'he_detonate') continue;
            const frame = replay.frames[ev.frameIdx];
            if (!frame) continue;
            for (const smoke of frame.world.smokes) {
                const dx = ev.x - smoke.x;
                const dy = ev.y - smoke.y;
                if (dx * dx + dy * dy <= radiusSq) {
                    breaks.push({
                        smokeEntityId: smoke.entityId,
                        frameIdx: ev.frameIdx,
                        x: ev.x,
                        y: ev.y,
                    });
                }
            }
        }
        return breaks;
    }, [replay]);

    // Timeline events for event markers
    const timelineEvents = useMemo(() => {
        if (!replay) return [];
        const baseEvents = replay.events
            .filter(e => ['kill', 'bomb_plant', 'bomb_defused', 'bomb_exploded'].includes(e.type))
            .map(e => ({
                frameIdx: e.frameIdx,
                type: e.type as 'kill' | 'bomb_plant' | 'bomb_defused' | 'bomb_exploded' | 'smoke_break',
                team: e.type === 'kill' ? replay.roster[(e as KillEvent).victimSlot]?.team : undefined,
            }));
        // Collapse smoke breaks at the same frame (one HE hitting two overlapping
        // smokes is one event, one marker).
        const seenFrames = new Set<number>();
        const breakEvents = smokeBreaks
            .filter(b => {
                if (seenFrames.has(b.frameIdx)) return false;
                seenFrames.add(b.frameIdx);
                return true;
            })
            .map(b => ({
                frameIdx: b.frameIdx,
                type: 'smoke_break' as const,
                team: undefined,
            }));
        return [...baseEvents, ...breakEvents];
    }, [replay, smokeBreaks]);

    // Per-frame snapshot of each player's last non-knife weapon. Used by the side scoreboard
    // to display the holstered main weapon when the player currently has a knife out.
    // A "main weapon" is a gun (1-51) or Zeus (61) — grenades, C4, and the knife itself don't count.
    const mainWeaponByFrame = useMemo<number[][] | null>(() => {
        if (!replay) return null;
        const numPlayers = replay.roster.length;
        const numFrames = replay.frames.length;
        const result: number[][] = new Array(numFrames);
        const running = new Array<number>(numPlayers).fill(0);
        for (let f = 0; f < numFrames; f++) {
            const frame = replay.frames[f];
            for (let p = 0; p < numPlayers; p++) {
                const wid = frame.players[p]?.weaponId ?? 0;
                if ((wid >= 1 && wid <= 51) || wid === 61) {
                    running[p] = wid;
                }
            }
            result[f] = running.slice();
        }
        return result;
    }, [replay]);

    // Each hurt event's real HP loss: capped at the victim's remaining HP, so an AWP body shot
    // (448 raw) counts 100 and shotgun pellets landing in one tick cannot add up past it.
    const hurtEvents = useMemo(() => {
        if (!replay) return [];
        const remaining = replay.roster.map((_, i) => replay.frames[0]?.players[i]?.hp ?? 100);
        const out: { frameIdx: number; attacker: number | null; victim: number; dealt: number }[] = [];
        for (const e of replay.events) {
            if (e.type !== 'player_hurt') continue;
            const h = e as PlayerHurtEvent;
            const dealt = Math.max(0, Math.min(h.hpDamage, remaining[h.victimSlot] ?? 0));
            remaining[h.victimSlot] = (remaining[h.victimSlot] ?? 0) - dealt;
            const enemy = h.attackerSlot !== null && !h.friendlyFire
                && replay.roster[h.attackerSlot]?.team !== replay.roster[h.victimSlot]?.team;
            out.push({ frameIdx: h.frameIdx, attacker: enemy ? h.attackerSlot : null, victim: h.victimSlot, dealt });
        }
        return out;
    }, [replay]);

    const hitWindow = Math.max(replay?.header.tickRate ?? 64, 1); // one second of frames at 64 Hz
    const { damageBySlot, recentHitBySlot } = useMemo(() => {
        const dealt: Record<number, number> = {};
        const recent: Record<number, { damage: number; fade: number }> = {};
        for (const h of hurtEvents) {
            if (h.frameIdx > currentFrame) break;
            if (h.attacker !== null) dealt[h.attacker] = (dealt[h.attacker] || 0) + h.dealt;
            const age = currentFrame - h.frameIdx;
            if (age < hitWindow && h.dealt > 0) {
                const prev = recent[h.victim];
                recent[h.victim] = { damage: (prev?.damage || 0) + h.dealt, fade: 1 - age / hitWindow };
            }
        }
        return { damageBySlot: dealt, recentHitBySlot: recent };
    }, [hurtEvents, currentFrame, hitWindow]);

    // Kill counts per player slot up to current frame
    const killCounts = useMemo(() => {
        if (!replay) return {};
        const counts: Record<number, number> = {};
        for (const e of replay.events) {
            if (e.type === 'kill' && e.frameIdx <= currentFrame) {
                const k = e as KillEvent;
                counts[k.killerSlot] = (counts[k.killerSlot] || 0) + 1;
            }
        }
        return counts;
    }, [replay, currentFrame]);

    if (error && !replay) {
        return (
            <div style={{ textAlign: 'center', padding: '60px', color: '#cc4444' }}>
                {error}
            </div>
        );
    }

    if (!replay) {
        return (
            <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>
                {loading ? `Loading round ${selectedRound}...` : 'No replay data available'}
            </div>
        );
    }

    const currentFrameData = replay.frames[currentFrame];
    const tTeamName = team1IsT ? team1Name : team2Name;
    const ctTeamName = team1IsT ? team2Name : team1Name;
    const tLogo = team1IsT ? team1Logo : team2Logo;
    const ctLogo = team1IsT ? team2Logo : team1Logo;

    // The odds at the playhead: the last swing step at or before it
    const swingNow = swingOn
        ? swingTrack.steps.reduce((cur, st) => (st.frame <= currentFrame ? st : cur), swingTrack.steps[0])
        : null;

    // Timer computation
    const tickRate = Math.max(replay.header.tickRate, 1);
    const currentTick = replay.frames[currentFrame]?.tick ?? 0;
    const startTick = replay.frames[0]?.tick ?? 0;
    const elapsedSecs = (currentTick - startTick) / tickRate;
    const isBombPlanted = bombPlantFrame !== null && currentFrame >= bombPlantFrame;
    const ROUND_TIME = 115;
    const timerDisplay = (() => {
        if (isBombPlanted) {
            const remaining = Math.max(0, 40 - (currentFrame - bombPlantFrame) / tickRate);
            const s = Math.ceil(remaining);
            return `0:${s.toString().padStart(2, '0')}`;
        }
        const remaining = Math.max(0, ROUND_TIME - elapsedSecs);
        const m = Math.floor(remaining / 60);
        const s = Math.floor(remaining % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    })();

    // Shared map area content
    const mapArea = (
        <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
        }}>
            {/* Score header — replaces old timer overlay */}
            <ReplayScoreHeader
                tTeamName={tTeamName}
                ctTeamName={ctTeamName}
                tScore={tScore}
                ctScore={ctScore}
                timerDisplay={timerDisplay}
                isBombPlanted={isBombPlanted}
                roundNumber={selectedRound}
                tLogo={tLogo}
                ctLogo={ctLogo}
            />
            {swingOn && swingNow && <ReplaySwingBar tPct={swingNow.t} decided={swingNow.decided} />}

            {/* Loading overlay */}
            {loading && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 20,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    pointerEvents: 'none',
                }}>
                    <span style={{ color: '#aaa', fontSize: '14px', fontWeight: 600 }}>
                        Loading round {selectedRound}...
                    </span>
                </div>
            )}

            {/* Canvas + kill feed container — positioned relative for the kill feed overlay */}
            <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <ReplayCanvas
                    replay={replay}
                    currentFrame={currentFrame}
                    mapName={cleanMapName}
                    playerNames={playerNames}
                    isBombPlanted={isBombPlanted}
                    bombPlantFrame={bombPlantFrame}
                    activeDefuse={activeDefuse}
                    bombExploded={bombExploded}
                    zoom={zoom}
                    panOffset={panOffset}
                    onZoomChange={setZoom}
                    onPanOffsetChange={setPanOffset}
                    isPanMode={isPanMode}
                    isAdmin={canUseSmokes}
                    onSmokeClick={handleSmokeClick}
                    smokeClipEntityIds={smokeClipEntityIds}
                    smokeBreaks={smokeBreaks}
                    mapControl={showMapControl ? mapControl.data : null}
                    mapControlLower={showMapControl ? mapControlLower.data : null}
                />

                {/* Map-control legend. The TOGGLE lives in the control bar below: as an overlay button
                    it sat on top of the map and cost screen space even when the feature was off. */}
                {canSeeMapControl && mapControl.supported && showMapControl && (
                    <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 5, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                        {showMapControl && mapControl.data && (
                            <div style={{
                                background: 'rgba(16,16,20,0.82)', border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 6, padding: '7px 9px', fontSize: '0.7rem', lineHeight: 1.7,
                                color: 'rgba(255,255,255,0.85)',
                            }}>
                                {([
                                    ['rgba(96,138,200,0.95)', 'CT hard'],
                                    ['rgba(72,96,140,0.95)', 'CT implicit'],
                                    ['rgba(198,152,60,0.95)', 'T hard'],
                                    ['rgba(150,116,52,0.95)', 'T implicit'],
                                    ['rgba(178,70,74,0.95)', 'Contested'],
                                ] as const).map(([c, label]) => (
                                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 12, height: 9, background: c, borderRadius: 2, display: 'inline-block' }} />
                                        {label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <ReplayKillFeed
                    kills={killFeedData}
                    currentFrame={currentFrame}
                />

                {/* Shows while Q/E is held, since the speed buttons do not change for a hold */}
                {hold && (
                    <div style={{
                        position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 6,
                        background: 'rgba(16,16,20,0.82)', border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 6, padding: '3px 10px', pointerEvents: 'none',
                        color: '#f0c060', fontSize: '0.75rem', fontWeight: 700,
                        fontFamily: '"Consolas", "Menlo", monospace',
                    }}>
                        {hold === 'rewind' ? `◀◀ ${speed}x` : `▶▶ ${speed * 2}x`}
                    </div>
                )}

                {showShortcuts && <ReplayShortcutsHelp onClose={() => setShowShortcuts(false)} canCopyLink={canCopyLink} />}

                {linkToast && (
                    <div style={{
                        position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 30,
                        backgroundColor: 'rgba(0, 0, 0, 0.85)', color: '#e2e8f0', padding: '8px 16px',
                        borderRadius: 6, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                        border: '1px solid rgba(255, 255, 255, 0.1)', pointerEvents: 'none',
                    }}>
                        {linkToast}
                    </div>
                )}

                {/* Smoke detail panel overlay */}
                {canUseSmokes && selectedSmokeThrow && selectedSmokeStart && (
                    <SmokeDetailPanel
                        smokeThrow={selectedSmokeThrow}
                        playerName={(() => {
                            const entry = replay.roster[selectedSmokeThrow.throwerSlot];
                            return entry ? playerNames[entry.steamId] || `Player ${selectedSmokeThrow.throwerSlot + 1}` : `Player ${selectedSmokeThrow.throwerSlot + 1}`;
                        })()}
                        team={replay.roster[selectedSmokeThrow.throwerSlot]?.team || 'T'}
                        throwerSteamId={replay.roster[selectedSmokeThrow.throwerSlot]?.steamId || '0'}
                        throwTick={selectedSmokeThrow.absoluteTick}
                        onClose={() => setSelectedSmokeEntityId(null)}
                        gameId={gameId}
                        matchId={matchId}
                        season={season}
                        roundNumber={selectedRound}
                        mapName={cleanMapName}
                        existingClipRequest={selectedSmokeClipRequest}
                        onClipRequested={(req) => {
                            setClipRequests(prev => [...prev, req]);
                        }}
                    />
                )}
            </div>

            {/* Controls — sit inside mapArea so they only span the map's width, not under the side scoreboards */}
            <ReplayControls
                isPlaying={isPlaying}
                onPlayPause={handlePlayPause}
                speed={speed}
                onSpeedChange={setSpeed}
                currentFrame={currentFrame}
                totalFrames={replay.frames.length}
                onSeek={handleSeek}
                replay={replay}
                selectedRound={selectedRound}
                roundCount={roundCount}
                onRoundChange={setSelectedRound}
                roundEnds={roundEnds}
                team1Id={team1Id}
                team2Id={team2Id}
                timelineEvents={timelineEvents}
                isBombPlanted={isBombPlanted}
                layout={effectiveLayout}
                onLayoutToggle={() => setLayout(l => l === 'split' ? 'stacked' : 'split')}
                zoom={zoom}
                onZoomChange={setZoom}
                isPanMode={isPanMode}
                onPanModeToggle={() => setIsPanMode(prev => !prev)}
                onResetView={handleResetView}
                isFullscreen={isFullscreen}
                onFullscreenToggle={handleFullscreenToggle}
                mapControlSupported={canSeeMapControl && mapControl.supported}
                showMapControl={showMapControl}
                onMapControlToggle={() => setShowMapControl(v => !v)}
                mapControlLoading={mapControl.loading}
                swingAvailable={swingTrack !== null}
                showSwing={showSwing}
                onSwingToggle={toggleSwing}
                showShortcuts={showShortcuts}
                onShortcutsToggle={() => setShowShortcuts(v => !v)}
                onCopyLink={canCopyLink ? handleCopyLink : undefined}
            />
        </div>
    );

    // Scoreboard props shared between layouts
    const scoreboardProps = {
        frame: currentFrameData,
        roster: replay.roster,
        playerNames,
        killCounts,
        mainWeaponBySlot: mainWeaponByFrame?.[currentFrame],
        damageBySlot,
        recentHitBySlot,
    };
    const roundEnd = roundEnds?.[selectedRound];
    const tBuy = { buyType: roundEnd?.tBuyType, equipValue: roundEnd?.tEquipValue };
    const ctBuy = { buyType: roundEnd?.ctBuyType, equipValue: roundEnd?.ctEquipValue };

    return (
        <div ref={containerRef} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: isFullscreen ? '100vh' : 'calc(100vh - 60px)',
            minHeight: 400,
            backgroundColor: 'var(--bg-primary, #1a1a1a)',
            userSelect: 'none',
            WebkitUserSelect: 'none',
        }}>
            {/* Main area */}
            {effectiveLayout === 'mobile' ? (
                <>
                    {/* Mobile layout: map full-width, compact scoreboards below */}
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {mapArea}
                    </div>
                    {currentFrameData && (
                        <div style={{
                            display: 'flex',
                            gap: 0,
                            borderTop: '1px solid #333',
                            backgroundColor: 'var(--bg-primary, #1a1a1a)',
                            flexShrink: 0,
                        }}>
                            <ReplayScoreboard
                                {...scoreboardProps}
                                side="T"
                                {...tBuy}
                                teamName={tTeamName}
                                layout="mobile"
                            />
                            <div style={{ width: 1, backgroundColor: '#333', flexShrink: 0 }} />
                            <ReplayScoreboard
                                {...scoreboardProps}
                                side="CT"
                                {...ctBuy}
                                teamName={ctTeamName}
                                layout="mobile"
                            />
                        </div>
                    )}
                </>
            ) : (
                <div style={{
                    display: 'flex',
                    gap: 0,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}>
                    {effectiveLayout === 'split' ? (
                        <>
                            {/* Layout A (CS2Lens): T left, map center, CT right */}
                            {currentFrameData && (
                                <ReplayScoreboard
                                    {...scoreboardProps}
                                    side="T"
                                    {...tBuy}
                                    position="left"
                                    teamName={tTeamName}
                                    layout="split"
                                />
                            )}
                            {mapArea}
                            {currentFrameData && (
                                <ReplayScoreboard
                                    {...scoreboardProps}
                                    side="CT"
                                    {...ctBuy}
                                    position="right"
                                    teamName={ctTeamName}
                                    layout="split"
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {/* Layout B (Noesis): map left, both scoreboards stacked right */}
                            {mapArea}
                            <div style={{
                                width: 230,
                                display: 'flex',
                                flexDirection: 'column',
                                borderLeft: '1px solid var(--border-primary, #333)',
                                backgroundColor: 'var(--bg-primary, #1a1a1a)',
                            }}>
                                {currentFrameData && (
                                    <ReplayScoreboard
                                        {...scoreboardProps}
                                        side="T"
                                        {...tBuy}
                                        teamName={tTeamName}
                                        layout="stacked"
                                    />
                                )}
                                <div style={{ height: 1, backgroundColor: '#333', margin: '0 4px' }} />
                                {currentFrameData && (
                                    <ReplayScoreboard
                                        {...scoreboardProps}
                                        side="CT"
                                        {...ctBuy}
                                        teamName={ctTeamName}
                                        layout="stacked"
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

        </div>
    );
};

export default ReplayViewer;
